import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useIsJobOwner } from '@/hooks/useJobAssignments';
import { useTeamMembers } from '@/hooks/useTeamManagement';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { UserPlus, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface AssignRecruiterSelectProps {
  applicationId: string;
  assignedTo: string | null;
  jobId?: string;
  /** Compact mode shows just an avatar or icon */
  compact?: boolean;
}

export function AssignRecruiterSelect({ applicationId, assignedTo, jobId, compact }: AssignRecruiterSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: members } = useTeamMembers();
  const { role } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const isJobOwner = useIsJobOwner(jobId);

  const isAdmin = role === 'org_owner' || role === 'org_admin';
  const canAssign = isAdmin || isJobOwner;

  const mutation = useMutation({
    mutationFn: async (userId: string | null) => {
      const { error } = await supabase
        .from('applications')
        .update({ assigned_to: userId })
        .eq('id', applicationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['job-candidates'] });
      toast({ title: t('hire.assignRecruiter.assignmentUpdated') });
      setOpen(false);
    },
    onError: () => {
      toast({ title: t('hire.assignRecruiter.assignmentFailed'), variant: 'destructive' });
    },
  });

  // Only admins can reassign
  const assignedMember = members?.find((m) => m.user_id === assignedTo);
  const initials = assignedMember?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  if (!canAssign) {
    // Non-admins just see the badge
    if (!assignedMember) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Avatar className="h-5 w-5 rounded-md">
              <AvatarFallback className="rounded-md bg-primary/10 text-primary text-[9px] font-bold">{initials}</AvatarFallback>
            </Avatar>
            {!compact && <span className="truncate max-w-[100px]">{assignedMember.full_name}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{t('hire.assignRecruiter.assignedTo', { name: assignedMember.full_name })}</TooltipContent>
      </Tooltip>
    );
  }

  // Eligible assignees: active recruiters, hiring managers, and admins
  const eligibleMembers = (members || []).filter(
    (m) => m.status === 'active' && ['org_owner', 'org_admin', 'recruiter', 'hiring_manager'].includes(m.role)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {assignedMember ? (
          <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md px-1.5 py-0.5 hover:bg-muted/60">
            <Avatar className="h-5 w-5 rounded-md">
              <AvatarFallback className="rounded-md bg-primary/10 text-primary text-[9px] font-bold">{initials}</AvatarFallback>
            </Avatar>
            {!compact && <span className="truncate max-w-[100px]">{assignedMember.full_name}</span>}
          </button>
        ) : (
          <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md px-2 py-1 hover:bg-muted/60 border border-dashed border-border">
            <UserPlus className="w-3.5 h-3.5" />
            {!compact && <span>{t('hire.assignRecruiter.assign')}</span>}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <p className="text-xs font-semibold text-foreground px-2 py-1.5">{t('hire.assignRecruiter.assignTo')}</p>
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          {eligibleMembers.map((m) => (
            <button
              key={m.user_id}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted/60 transition-colors ${m.user_id === assignedTo ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}`}
              onClick={() => mutation.mutate(m.user_id)}
              disabled={mutation.isPending}
            >
              <Avatar className="h-6 w-6 rounded-md">
                <AvatarFallback className="rounded-md bg-muted text-muted-foreground text-[10px] font-bold">
                  {m.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-start truncate">
                <span className="text-xs">{m.full_name}</span>
              </div>
            </button>
          ))}
        </div>
        {assignedTo && (
          <button
            className="w-full flex items-center gap-2 px-2 py-1.5 mt-1 rounded-md text-xs text-destructive hover:bg-destructive/10 transition-colors border-t border-border"
            onClick={() => mutation.mutate(null)}
            disabled={mutation.isPending}
          >
            <X className="w-3.5 h-3.5" /> {t('hire.assignRecruiter.unassign')}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
